import React, { useEffect, useState, useRef } from "react";
import axios from "../../api/axios";
import "./styles/AdminReviews.css";


const emptyReview = {
  id: "new",
  rating: 5,
  reviewer_name: "",
  review_text: "",
  is_active: true,
  service_area_id: null,
};


const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [csrfToken, setCsrfToken] = useState("");
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const cardRefs = useRef({});
  const positions = useRef({});
  const originalOrderRef = useRef([]);
  const [draggedId, setDraggedId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [serviceAreas, setServiceAreas] = useState([]);



  const disableAllDrag = !!editingReviewId;

  // Grab CSRF token on mount
  useEffect(() => {
    const token = document.cookie
      .split("; ")
      .find(row => row.startsWith("csrfToken="))
      ?.split("=")[1];
    if (token) setCsrfToken(token);
  }, []);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("/api/public/reviews");
      console.log("Save response:", data);

      setReviews(data.reviews || []);
    } catch (err) {
      setError("Failed to load reviews.");
    } finally {
      setLoading(false);
    }
  };

  // --- Delete ---
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    if (!csrfToken) return setError("CSRF token missing.");
    try {
      await axios.delete(`/api/admin/reviews/${id}`, {
        withCredentials: true,
        headers: { "x-csrf-token": csrfToken },
      });
      setReviews(reviews.filter(r => r.id !== id));
    } catch {
      setError("Failed to delete review.");
    }
  };

  useEffect(() => {
    const fetchServiceAreas = async () => {
      try {
        const res = await axios.get("/api/public/service-areas", { withCredentials: true });
        const activeAreas = (res.data.service_areas || []).filter(a => a.is_active);
        setServiceAreas(activeAreas);
      } catch (err) {
        console.error("🔥 Error fetching service areas:", err);
      }
    };
    fetchServiceAreas();
  }, []);


  // --- Inline Add ---
  const handleAdd = () => {
    setAddingNew(true);
    setEditForm({ ...emptyReview });
    setEditingReviewId("new");
    // Only allow one empty at a time
    if (!reviews.some(r => r.id === "new")) {
      setReviews([...reviews, { ...emptyReview }]);
    }
  };

  // --- Inline Edit ---
  const handleEdit = (review) => {
    setEditForm({ ...review });
    setEditingReviewId(review.id);
  };

  // --- Cancel ---
  const handleCancel = () => {
    setAddingNew(false);
    setEditForm(null);
    setEditingReviewId(null);
    // Remove unsaved new review
    setReviews((old) => old.filter((r) => r.id !== "new"));
  };

  const handleSave = async () => {
  if (!csrfToken) return setError("CSRF token missing.");
  if (!editForm.reviewer_name.trim() || !editForm.review_text.trim()) {
    setError("Name and review are required.");
    return;
  }
  setError("");

  try {
    // Clone the editForm so we don't mutate state directly
    let payload = { ...editForm };
    // If sort_order is undefined, null, empty string, or 0, remove it so backend will default
    if (!payload.sort_order) {
      delete payload.sort_order;
    }

    let res;
    if (editingReviewId === "new") {
      // Add
      res = await axios.post("/api/admin/reviews", payload, {
        withCredentials: true,
        headers: { "x-csrf-token": csrfToken },
      });
      const review = res.data.review;
      if (!review || typeof review.id === "undefined") {
        setError("Failed to save review: Invalid server response.");
        return;
      }
      setReviews((old) =>
        old.filter((r) => r && r.id !== "new").concat(review)
      );
    } else {
      // Edit
      res = await axios.put(`/api/admin/reviews/${editingReviewId}`, payload, {
        withCredentials: true,
        headers: { "x-csrf-token": csrfToken },
      });
      const review = res.data.review;
      if (!review || typeof review.id === "undefined") {
        setError("Failed to save review: Invalid server response.");
        return;
      }
      setReviews((old) =>
        old.map((r) => (r && r.id === editingReviewId ? review : r))
      );
    }
    setEditForm(null);
    setEditingReviewId(null);
  } catch {
    setError("Failed to save review.");
  }
};


const recordPositions = (list) => {
  positions.current = {};
  list.forEach(r => {
    const node = cardRefs.current[r.id];
    if (node) {
      positions.current[r.id] = node.getBoundingClientRect();
    }
  });
};

const playFLIP = (list) => {
  list.forEach(r => {
    const node = cardRefs.current[r.id];
    const prev = positions.current[r.id];
    if (node && prev) {
      const next = node.getBoundingClientRect();
      const dx = prev.left - next.left;
      const dy = prev.top - next.top;
      if (dx !== 0 || dy !== 0) {
        node.style.transition = "none";
        node.style.transform = `translate(${dx}px, ${dy}px)`;
        requestAnimationFrame(() => {
          node.style.transition = "transform 250ms cubic-bezier(.23,1.09,.67,.95)";
          node.style.transform = "";
        });
      }
    }
  });
};

function isMobile() {
  return window.innerWidth <= 700; // Match your media query
}

const moveReview = (index, direction) => {
  const newIdx = index + direction;
  if (newIdx < 0 || newIdx >= reviews.length) return;
  const newReviews = [...reviews];
  [newReviews[index], newReviews[newIdx]] = [newReviews[newIdx], newReviews[index]];
  setReviews(newReviews);

  // Immediately update backend order
  const reorderPayload = newReviews.map((r, idx) => ({
    id: r.id,
    sort_order: idx + 1
  }));
  axios.patch(
    "/api/admin/reviews/reorder",
    { order: reorderPayload },
    { headers: { "x-csrf-token": csrfToken }, withCredentials: true }
  ).catch(() => setError("Failed to update order."));
};

const moveReviewTo = (index, targetIdx) => {
  if (index === targetIdx || index < 0 || index >= reviews.length || targetIdx < 0 || targetIdx >= reviews.length) return;
  const newReviews = [...reviews];
  const [moved] = newReviews.splice(index, 1);
  newReviews.splice(targetIdx, 0, moved);
  setReviews(newReviews);

  // Immediately update backend order
  const reorderPayload = newReviews.map((r, idx) => ({
    id: r.id,
    sort_order: idx + 1
  }));
  axios.patch(
    "/api/admin/reviews/reorder",
    { order: reorderPayload },
    { headers: { "x-csrf-token": csrfToken }, withCredentials: true }
  ).catch(() => setError("Failed to update order."));
};


  // --- Render ---
  return (
    <div className="admin-messages-container">
      <h2 className="messages-title">Manage Reviews</h2>
      <div style={{ marginBottom: 20 }}>
        <button className="btn add-btn" onClick={handleAdd} disabled={editingReviewId === "new"}>
          <i className="fa-solid fa-plus"></i> Add Review
        </button>
      </div>
      {error && <div className="admin-reviews-error">{error}</div>}
        {loading ? (
        <p className="messages-loading">Loading reviews...</p>
        ) : reviews.length === 0 ? (
        <p className="messages-empty">No reviews found.</p>
        ) : (
        (() => {
            // Pre-filter out the 'new' shell review except while editing/adding
            // Pre-filter out the 'new' shell review except while editing/adding
            let visibleReviews = Array.isArray(reviews)
            ? reviews.filter(r => !!r && (r.id !== "new" || editingReviewId === "new"))
            : [];

            // Move "new" review to the front if present
            const newReviewIdx = visibleReviews.findIndex(r => r.id === "new");
            if (newReviewIdx !== -1) {
            const [newReview] = visibleReviews.splice(newReviewIdx, 1);
            visibleReviews = [newReview, ...visibleReviews];
            }
            // Chunk reviews into arrays of 4
            const chunkArray = (arr, size) => {
            const res = [];
            for (let i = 0; i < arr.length; i += size) {
                res.push(arr.slice(i, i + size));
            }
            return res;
            };
            return (
                <>
            <div style={{ marginBottom: '2.5rem', display: "flex", alignItems: "center", gap: 8, justifyContent: 'center' }}>
            <i className="fa-solid fa-bars" style={{ opacity: 0.7 }} aria-hidden="true" />
            <span style={{ fontSize: 15, color: "#555" }}>
              Drag and drop to reorder reviews.
            </span>
          </div>
            <div className="testimonial-row-wrapper">
            {chunkArray(visibleReviews, 4).map((row, i) => (
            <div className="testimonial-row" key={i}>
                {row.map((review, idx) => {
                   const absoluteIdx = i * 4 + idx;
                const editing = editingReviewId === review.id;
                const disableReorder = editingReviewId === "new";
                const isNewReview = review.id === "new";
                    return (
                        <React.Fragment key={review.id}>
                        <div
      className={`review-card admin ${draggedId === review.id ? " dragging" : ""}`}
  key={review.id}
  ref={el => cardRefs.current[review.id] = el}
  draggable={!disableAllDrag && review.id !== "new"}
  onDragStart={e => {
    if (disableAllDrag || review.id === "new") {
      e.preventDefault();
      return;
    }
    setDraggedId(review.id);
    originalOrderRef.current = reviews.map(r => r.id);
  }}
  onDragOver={e => {
    if (disableAllDrag || review.id === "new" || draggedId === null) return;
    e.preventDefault();

    // Get current index and new index
    const currentIndex = reviews.findIndex(r => r.id === draggedId);
    const targetIndex = reviews.findIndex(r => r.id === review.id);

    if (currentIndex === -1 || currentIndex === targetIndex) return;

    const newReviews = [...reviews];
    const [moved] = newReviews.splice(currentIndex, 1);
    newReviews.splice(targetIndex, 0, moved);

    setReviews(newReviews);
    // Do NOT update draggedId here!
  }}
  onDrop={async () => {
    setDraggedId(null);
    // Only update if order changed
    const currentOrder = reviews.map(r => r.id);
    const originalOrder = originalOrderRef.current;
    const orderChanged =
      currentOrder.length !== originalOrder.length ||
      currentOrder.some((id, idx) => id !== originalOrder[idx]);

    if (!orderChanged) return;

    const reorderPayload = reviews.map((r, idx) => ({
      id: r.id,
      sort_order: idx + 1
    }));

    try {
      await axios.patch(
        "/api/admin/reviews/reorder",
        { order: reorderPayload },
        { headers: { "x-csrf-token": csrfToken }, withCredentials: true }
      );
      // Optionally re-fetch reviews here if you want to sync with backend
      // fetchReviews();
    } catch {
      setError("Failed to update order.");
    }
  }}

>
                        {!disableAllDrag && review.id !== "new" && (
                        <span className="drag-handle" title="Drag to reorder" aria-label="Drag to reorder">
                            <i className="fa-solid fa-bars"></i>
                        </span>
                        )}

                        {/* Editable Card */}
                        {editing ? (
                            <>
                            <div className="review-content">
                                <select
                                value={editForm.rating}
                                onChange={(e) =>
                                    setEditForm(f => ({
                                    ...f,
                                    rating: Number(e.target.value)
                                    }))
                                }
                                >
                                {[5, 4, 3, 2, 1].map(star => (
                                    <option key={star} value={star}>
                                    {`${"⭐".repeat(star)} (${star})`}
                                    </option>
                                ))}
                                </select>
                            </div>
                            <div className="review-content">
                                <textarea
                                rows={4}
                                value={editForm.review_text}
                                placeholder="Review text"
                                onChange={e =>
                                    setEditForm(f => ({
                                    ...f,
                                    review_text: e.target.value
                                    }))
                                }
                                />
                                <input
                                type="text"
                                value={editForm.reviewer_name}
                                placeholder="Reviewer name"
                                onChange={e =>
                                    setEditForm(f => ({
                                    ...f,
                                    reviewer_name: e.target.value
                                    }))
                                }
                                />
                                <div style={{ margin: "8px 0" }}>
                                <label>
                                    <input
                                    type="checkbox"
                                    checked={!!editForm.is_active}
                                    onChange={e =>
                                        setEditForm(f => ({
                                        ...f,
                                        is_active: e.target.checked
                                        }))
                                    }
                                    />{" "}
                                    Show on site
                                </label>
                                </div>

                                <div style={{ margin: "8px 0" }}>
                                  <label>
                                    <span style={{ display: "block", marginBottom: 4 }}>Service Area</span>
                                    <select
                                      value={editForm.service_area_id || ""}
                                      onChange={(e) =>
                                        setEditForm((f) => ({
                                          ...f,
                                          service_area_id: e.target.value ? Number(e.target.value) : null,
                                        }))
                                      }
                                    >
                                      <option value="">— None —</option>
                                      {serviceAreas.map((area) => (
                                        <option key={area.id} value={area.id}>
                                          {area.city}, {area.state}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                </div>

                            </div>
                            <div className="review-card-actions">
                                <button
                                className="btn save-btn"
                                onClick={handleSave}
                                style={{ marginRight: 8 }}
                                >
                                <i className="fa-solid fa-floppy-disk"></i> Save
                                </button>
                                <button
                                className="btn cancel-btn"
                                onClick={handleCancel}
                                >
                                <i className="fa-solid fa-xmark"></i> Cancel
                                </button>
                            </div>
                            </>
                        ) : (
                            <>
                              {review.is_active !== 1 && review.is_active !== true && (
                                <div
                                  className="review-hidden-icon"
                                  title="Hidden from public site"
                                  style={{
                                    opacity: 0.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    marginBottom: 2,
                                    fontSize: 18
                                  }}
                                >
                                  <i className="fa-solid fa-eye-slash"></i>
                                  <span style={{ fontSize: 13, color: "#888" }}>(Hidden)</span>
                                </div>
                              )}
                            <div className="review-content">
                                <p>
                                {Array.from({ length: review.rating }).map(
                                    (_, idx) => "⭐"
                                )}
                                </p>
                            </div>
<div className="review-content">
  {/* Hidden/Visible Icon above the name if review is hidden */}

  <p>
    {review.review_text}
    <br />
    <br />
    <strong>{review.reviewer_name}</strong>
    <br />
  </p>
  
</div>

                            {!editing && !addingNew && (
                              <div className="reorder-buttons" style={{ display: isMobile() ? 'flex' : 'none', gap: 4 }}>
                                 <button
                                    className="btn reorder-btn"
                                    onClick={() => moveReviewTo(absoluteIdx, 0)}
                                    disabled={absoluteIdx === 0}
                                    aria-label="Move to top"
                                    tabIndex={0}
                                  >
                                    <i className="fa-solid fa-arrow-up-short-wide"></i>
                                  </button>
                                  <button
                                    className="btn reorder-btn"
                                    onClick={() => moveReview(absoluteIdx, -1)}
                                    disabled={absoluteIdx === 0}
                                    aria-label="Move up"
                                    tabIndex={0}
                                  >
                                    <i className="fa-solid fa-arrow-up"></i>
                                  </button>
                                  <button
                                    className="btn reorder-btn"
                                    onClick={() => moveReview(absoluteIdx, 1)}
                                    disabled={absoluteIdx === reviews.length - 1}
                                    aria-label="Move down"
                                    tabIndex={0}
                                  >
                                    <i className="fa-solid fa-arrow-down"></i>
                                  </button>
                                  <button
                                    className="btn reorder-btn"
                                    onClick={() => moveReviewTo(absoluteIdx, reviews.length - 1)}
                                    disabled={absoluteIdx === reviews.length - 1}
                                    aria-label="Move to bottom"
                                    tabIndex={0}
                                  >
                                    <i className="fa-solid fa-arrow-down-wide-short"></i>
                                  </button>

                              </div>
                            )}

                            <button
                                className="btn edit-btn"
                                onClick={() => handleEdit(review)}
                                style={{ marginTop: 8, marginRight: 8 }}
                            >
                                <i className="fa-solid fa-pen-to-square"></i> Edit
                            </button>
                            <button
                                className="btn delete-btn"
                                onClick={() => handleDelete(review.id)}
                                style={{ marginTop: 8 }}
                            >
                                <i className="fa-solid fa-xmark"></i> Delete
                            </button>
                            </>
                        )}

                        </div>
                        {isNewReview && idx === 0 && i === 0 && (
                        <div className="gallery-vertical-separator" aria-hidden="true" style={{margin: '0 7px'}} />
                        )}
                        </React.Fragment>
                    );
                    })}
                </div>
                
                ))}
            </div>
            </>
            );
        })()
        )}

    </div>
  );
};

export default AdminReviews;
